/**
 * The caller's invite link, built from their metasite so the signup it produces is ATTRIBUTED to
 * them. Shared 1:1 with the native apps — iOS `SymphonyData.referralShareLink`, Android
 * `SymphonyApi.referralShareLink`, and the server's `referrals` native-data action all build the
 * same string, so an invite earns the same credit whoever sent it.
 *
 * Web was the lone surface sharing an UNATTRIBUTED link (`APP_SMART_LINK`, a bare install
 * smart-link with no referrer in it), which is why a web share earned the sharer nothing.
 *
 * ⚠️ The metasite must stay a PATH segment, never a query param. The links service generates the
 * install payload from `pathParams` only, so `?deeplink=…` is dropped and the referrer is lost
 * silently — see the long note on the server's `referrals` action for the measurements.
 *
 * Returns '' for a caller with no metasite. That empty string is meaningful: callers must treat it
 * as "we do not know this person's link yet" and refuse to offer it, exactly as iOS disables its
 * share CTA on `shareLink.isEmpty`. Handing out a half-known link is worse than offering none —
 * the recipient signs up and the referral attributes to nobody.
 */
const REFERRAL_LINK_HOST = 'https://symphony.wix.app';

/** Where a visitor who cannot open the app is sent. Hard-coded on purpose: this URL is pasted into
 *  someone else's chat, so it can never be derived from the request host (a dev origin would ship). */
export const SYMPHONY_WEB_ORIGIN = 'https://symphony.wix.com';

/**
 * Two links-service parameters, and BOTH are required — `d` alone silently breaks phones.
 *
 * `d` names where a visitor who cannot open the app is sent, and without it that visitor is passed to the
 * vendor's install page and on to a generic marketing page which discards the referrer.
 *
 * `s` (`suggestMobile`) is what keeps `d` off phones. The service resolves a redirect in a fixed order,
 * and `d` is consulted for EVERY platform: `mobileUrl.filter(isMobileOS).orElse(desktopUrl)`. The
 * store-link resolver ahead of it is gated on `suggestMobile && isMobileOS`, so with `s` unset a phone
 * without the app falls through to `d` and lands on the web instead of the App Store — losing the one
 * attribution path that works today. With `s` set, a phone takes the store link (referrer preserved in
 * its `?deeplink=`) and only a desktop reaches `d`.
 *
 * Verified against production per platform: iPhone and Android reach their stores carrying the referrer,
 * a desktop reaches our signup carrying the id.
 *
 * Neither disturbs a phone that HAS the app: it intercepts the universal link by host + path before any
 * request is made, and both apps read the referrer from the PATH, passing unknown query keys through
 * (`jotActionTarget` / `jotActionForSegments`).
 */
const SUGGEST_MOBILE = 's=true';

function desktopFallbackUrl(metaSiteId: string): string {
  return `${SYMPHONY_WEB_ORIGIN}/welcome?ref=${encodeURIComponent(metaSiteId)}`;
}

/**
 * The invite link bare — for a target that can only ever be reached from a phone, such as a QR code.
 * Naming a desktop destination there would encode a longer string into a denser code for a case that
 * cannot arise, and a code that is harder to scan is a worse invite. It needs no `s` either: with neither
 * parameter set, the service's own fallback resolver already hands a phone the store link.
 */
export function referralInstallLink(metaSiteId: string | undefined | null): string {
  return metaSiteId ? `${REFERRAL_LINK_HOST}/referral/${metaSiteId}` : '';
}

/**
 * A get-the-app link that carries an invite when there is one, and is otherwise unchanged.
 *
 * `fallback` is where this control points for someone who arrived on their own — a store page, the plain
 * install smart-link — and it stays the destination off a phone, because that is what `?d=` names.
 *
 * It routes through `symphony.wix.app` rather than pointing at a store directly, and that is the whole
 * mechanism: the referrer is recorded when the click passes through the links service, which then hands
 * the phone a store URL carrying it for the first-launch lookup. A store URL with the referrer appended
 * by us would skip the service, so nothing would be recorded.
 */
export function installLink(ref: string | null | undefined, fallback: string): string {
  if (!ref) return fallback;
  return `${referralInstallLink(ref)}?${SUGGEST_MOBILE}&d=${encodeURIComponent(fallback)}`;
}

export function referralShareLink(metaSiteId: string | undefined | null): string {
  if (!metaSiteId) return '';
  const d = encodeURIComponent(desktopFallbackUrl(metaSiteId));
  return `${referralInstallLink(metaSiteId)}?${SUGGEST_MOBILE}&d=${d}`;
}
